import React, { useCallback } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import { CheckCircleTwoTone, FileProtectOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { usePanelData } from './usePanelData';
import PanelCard from './PanelCard';

const { Text } = Typography;

/**
 * The approval tile (spec §7 + §10.1).
 *
 * Two numbers, and neither is decoration:
 *
 *  1. **Waiting on us** — customers who approved and sent corrections. Somebody
 *     has to look at those before the job can be converted (G3).
 *  2. **Waiting on customers** — sent estimates the customer CANNOT approve,
 *     because the shop's requirements are not met on their record. §10.1 makes
 *     this count a release obligation: the requirement gate is on by default, so
 *     without it "on by default" is a way to quietly stop taking approvals.
 *
 * Both come from `GET /approval-review/queue`, which computes readiness with the
 * same gate that will refuse the customer. Nothing is evaluated here.
 */

interface QueueEntry {
  estimate_id: number;
  estimate_number: string | null;
  return_count: number;
}

interface StalledEntry {
  estimate_id: number;
  estimate_number: string | null;
  missing: string[];
}

interface ApprovalPanelData {
  queue: QueueEntry[];
  stalled: { count: number; estimates: StalledEntry[] };
}

const EMPTY_STALLED = { count: 0, estimates: [] as StalledEntry[] };

const ApprovalReviewPanel: React.FC = () => {
  const navigate = useNavigate();

  const fetcher = useCallback(async (): Promise<ApprovalPanelData> => {
    const res = await api.get('/approval-review/queue');
    return {
      queue: Array.isArray(res?.data?.queue) ? res.data.queue : [],
      // An older server, or one that failed to compute the scan, must read as
      // zero rather than crash the whole dashboard panel.
      stalled: res?.data?.stalled ?? EMPTY_STALLED,
    };
  }, []);

  const { data, loading, error, reload } = usePanelData<ApprovalPanelData>(fetcher);
  const queue = data?.queue ?? [];
  const stalled = data?.stalled ?? EMPTY_STALLED;
  const stalledCount = Number(stalled.count ?? 0);
  const nothingToShow = queue.length === 0 && stalledCount === 0;

  return (
    <PanelCard
      title="Approvals"
      icon={<FileProtectOutlined style={{ color: '#1890ff' }} />}
      count={queue.length}
      loading={loading}
      error={error}
      isEmpty={nothingToShow}
      emptyText={
        <span>
          <CheckCircleTwoTone twoToneColor="#52c41a" /> All clear — nothing waiting on either side
        </span>
      }
      onRetry={reload}
      extra={
        <Button
          type="link"
          data-testid="open-approval-review"
          onClick={() => navigate('/owner/approval-review')}
        >
          Review
        </Button>
      }
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 12,
          }}
        >
          <div data-testid="awaiting-review-count">
            <Text style={{ fontSize: 22, fontWeight: 600, display: 'block', lineHeight: 1.1 }}>
              {queue.length}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              waiting on us
            </Text>
          </div>
          <div data-testid="stalled-count">
            <Text
              style={{
                fontSize: 22,
                fontWeight: 600,
                display: 'block',
                lineHeight: 1.1,
                color: stalledCount > 0 ? '#d46b08' : undefined,
              }}
            >
              {stalledCount}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {stalledCount > 0 ? (
                <span>
                  <WarningOutlined style={{ color: '#d46b08' }} /> can&apos;t be approved yet
                </span>
              ) : (
                <span>can&apos;t be approved yet</span>
              )}
            </Text>
          </div>
        </div>

        {stalledCount > 0 && (
          <List
            size="small"
            dataSource={stalled.estimates.slice(0, 3)}
            renderItem={(est) => (
              <List.Item
                style={{ cursor: 'pointer', minHeight: 44, paddingInline: 0 }}
                onClick={() => navigate(`/owner/estimates/${est.estimate_id}`)}
              >
                <Space wrap size={6}>
                  <Text strong style={{ fontSize: 13 }}>
                    {est.estimate_number ?? `Estimate #${est.estimate_id}`}
                  </Text>
                  {est.missing.slice(0, 2).map((label) => (
                    <Tag key={label} color="orange" style={{ marginInlineEnd: 0 }}>
                      {label}
                    </Tag>
                  ))}
                  {est.missing.length > 2 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {`+${est.missing.length - 2} more`}
                    </Text>
                  )}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Space>
    </PanelCard>
  );
};

export default ApprovalReviewPanel;
